import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { FranchisorChannels_ChannelRow } from '../contracts/FranchisorChannels_ChannelRow'
import type { FranchisorChannels_ListResponseDados } from '../contracts/FranchisorChannels_ListResponse'
import type { FranchisorChannels_SaveInput } from '../contracts/FranchisorChannels_SaveInput'
import type { FranchisorChannels_ToggleInput } from '../contracts/FranchisorChannels_ToggleInput'
import type { FranchisorChannels_DeleteInput } from '../contracts/FranchisorChannels_DeleteInput'
import type { FranchisorChannels_DeleteResponseDados } from '../contracts/FranchisorChannels_DeleteResponse'
import type { FranchisorChannels_TestSendInput } from '../contracts/FranchisorChannels_TestSendInput'
import type { FranchisorChannels_TestSendResponseDados } from '../contracts/FranchisorChannels_TestSendResponse'
import type { FranchisorChannels_BindingRow } from '../contracts/FranchisorChannels_BindingRow'
import type { FranchisorChannels_TriggerRow } from '../contracts/FranchisorChannels_TriggerRow'
import type { FranchisorChannels_TriggersResponseDados } from '../contracts/FranchisorChannels_TriggersResponse'
import type { FranchisorChannels_TriggerSaveInput } from '../contracts/FranchisorChannels_TriggerSaveInput'
import type { FranchisorChannels_BindingResponseDados } from '../contracts/FranchisorChannels_BindingResponse'
import type { FranchisorChannels_TemplateRow } from '../contracts/FranchisorChannels_TemplateRow'
import type { FranchisorChannels_TemplatesResponseDados } from '../contracts/FranchisorChannels_TemplatesResponse'
import type { FranchisorChannels_TemplateSaveInput } from '../contracts/FranchisorChannels_TemplateSaveInput'
import type { FranchisorChannels_MessageRow } from '../contracts/FranchisorChannels_MessageRow'
import type { FranchisorChannels_MessagesResponseDados } from '../contracts/FranchisorChannels_MessagesResponse'
import type { FranchisorChannels_MessagesQueryInput } from '../contracts/FranchisorChannels_MessagesQueryInput'
import type { FranchisorChannels_PreviewInput } from '../contracts/FranchisorChannels_PreviewInput'
import type { FranchisorChannels_TemplateCreateInput } from '../contracts/FranchisorChannels_TemplateCreateInput'
import type { FranchisorChannels_PreviewResponseDados } from '../contracts/FranchisorChannels_PreviewResponse'
// Import side-effects: registers the contracts with the responseContract registry.
import '../contracts/FranchisorChannels_ChannelRow'
import '../contracts/FranchisorChannels_ListResponse'
import '../contracts/FranchisorChannels_SaveInput'
import '../contracts/FranchisorChannels_ToggleInput'
import '../contracts/FranchisorChannels_DeleteInput'
import '../contracts/FranchisorChannels_DeleteResponse'
import '../contracts/FranchisorChannels_TestSendInput'
import '../contracts/FranchisorChannels_TestSendResponse'
import '../contracts/FranchisorChannels_BindingRow'
import '../contracts/FranchisorChannels_TriggerRow'
import '../contracts/FranchisorChannels_TriggersResponse'
import '../contracts/FranchisorChannels_TriggerSaveInput'
import '../contracts/FranchisorChannels_BindingResponse'
import '../contracts/FranchisorChannels_TemplateRow'
import '../contracts/FranchisorChannels_TemplatesResponse'
import '../contracts/FranchisorChannels_TemplateSaveInput'
import '../contracts/FranchisorChannels_MessageRow'
import '../contracts/FranchisorChannels_MessagesResponse'
import '../contracts/FranchisorChannels_MessagesQueryInput'
import '../contracts/FranchisorChannels_PreviewInput'
import '../contracts/FranchisorChannels_TemplateCreateInput'
import '../contracts/FranchisorChannels_PreviewResponse'

/**
 * useFranchisorChannels — SOLE network owner for the S13 · B4 Full-only
 * console's `franchisor-channels/*` routes (app-api-service).
 *
 * REFUSALS ARRIVE AS HTTP 200. `sendError()` never touches the HTTP status
 * (MY_Controller.php), so `httpService` does not throw on its own and
 * `validateResponse()` does not even run — the ENVELOPE's `success:false` is
 * the only refusal signal. Every mutation here reads `success` from INSIDE
 * the body before trusting it, mirroring `useScheduledJobs`/`useTestClock`
 * (skill `billing-staff-console` §4). Named refusals per endpoint are
 * documented on the matching PHP contract (docs/billing-franchisor-channels.md).
 *
 * `api_key` is masked on every read (`ChannelRow.api_key`) — this composable
 * never retains a raw key anywhere; `saveChannel()` sends whatever the caller
 * typed (blank = "keep" per `SaveInput` docblock) and stores back only the
 * MASKED row the server echoes.
 *
 * Module-scope singleton, like every other composable in this domain.
 * Call `_reset()` in afterEach for test isolation.
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const channels = ref<FranchisorChannels_ChannelRow[]>([])
const triggers = ref<FranchisorChannels_TriggerRow[]>([])
const templates = ref<FranchisorChannels_TemplateRow[]>([])
const messages = ref<FranchisorChannels_MessageRow[]>([])
/** Idiomas em que uma variante pode ser escrita — vem junto com os templates. */
const templateLanguages = ref<string[]>([])

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

function upsertChannel(row: FranchisorChannels_ChannelRow): void {
  const idx = channels.value.findIndex((r) => r.id === row.id)
  if (idx === -1) {
    channels.value = [...channels.value, row]
  } else {
    channels.value = [...channels.value.slice(0, idx), row, ...channels.value.slice(idx + 1)]
  }
}

function upsertTemplate(row: FranchisorChannels_TemplateRow): void {
  const idx = templates.value.findIndex((r) => r.id === row.id)
  if (idx === -1) {
    templates.value = [...templates.value, row]
  } else {
    templates.value = [...templates.value.slice(0, idx), row, ...templates.value.slice(idx + 1)]
  }
}

/**
 * A trigger_save() response echoes only the single mutated BindingRow, never
 * the full TriggerRow it belongs to — splice it into that trigger's own
 * `bindings` array so the matrix re-renders from fresh state without a
 * round-trip back through listTriggers().
 */
function upsertBinding(row: FranchisorChannels_BindingRow): void {
  const tIdx = triggers.value.findIndex((t) => t.trigger_key === row.trigger_key)
  if (tIdx === -1) return
  const trigger = triggers.value[tIdx]
  const bindings = trigger.bindings ? [...trigger.bindings] : []
  const bIdx = bindings.findIndex((b) => b.id === row.id)
  if (bIdx === -1) {
    bindings.push(row)
  } else {
    bindings[bIdx] = row
  }
  triggers.value = [
    ...triggers.value.slice(0, tIdx),
    { ...trigger, bindings },
    ...triggers.value.slice(tIdx + 1),
  ]
}

// ── Public composable ────────────────────────────────────────────────────────

export function useFranchisorChannels() {
  /**
   * listChannels — GET franchisor-channels/list.
   */
  async function listChannels(): Promise<void> {
    const raw = await httpService.get(
      sistemaUrl('franchisor-channels/list'),
      { responseContract: 'FranchisorChannels_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { dados: FranchisorChannels_ListResponseDados }
    channels.value = resp.dados.rows
    log('useFranchisorChannels', 'listChannels', 'Canais carregados', { count: channels.value.length })
  }

  /**
   * saveChannel — POST franchisor-channels/save. `input.id` null/absent = create.
   * Refusals: 'channel_not_found' (update of a missing row).
   */
  async function saveChannel(input: FranchisorChannels_SaveInput): Promise<FranchisorChannels_ChannelRow> {
    const body: FranchisorChannels_SaveInput = {
      name: input.name,
      type: input.type,
      provider: input.provider,
      enabled: input.enabled ?? false,
      ...(input.id != null ? { id: input.id } : {}),
      ...(input.api_key != null && input.api_key !== '' ? { api_key: input.api_key } : {}),
      ...(input.sender != null && input.sender !== '' ? { sender: input.sender } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('franchisor-channels/save'),
      body,
      { inputContract: 'FranchisorChannels_SaveInput', responseContract: 'FranchisorChannels_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: FranchisorChannels_ListResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'franchisor_channel_save_failed')
    }
    const row = resp.dados.rows[0]
    upsertChannel(row)
    log('useFranchisorChannels', 'saveChannel', 'Canal salvo', { id: row.id, name: row.name })
    return row
  }

  /**
   * toggleChannel — POST franchisor-channels/toggle. Refusal: 'channel_not_found'.
   */
  async function toggleChannel(id: number, enabled: boolean): Promise<FranchisorChannels_ChannelRow> {
    const body: FranchisorChannels_ToggleInput = { id, enabled }
    const raw = await httpService.post(
      sistemaUrl('franchisor-channels/toggle'),
      body,
      { inputContract: 'FranchisorChannels_ToggleInput', responseContract: 'FranchisorChannels_ListResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: FranchisorChannels_ListResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'franchisor_channel_toggle_failed')
    }
    const row = resp.dados.rows[0]
    upsertChannel(row)
    log('useFranchisorChannels', 'toggleChannel', 'Canal alternado', { id: row.id, enabled: row.enabled })
    return row
  }

  /**
   * deleteChannel — POST franchisor-channels/delete.
   * Refusals: 'channel_not_found', 'channel_has_enabled_bindings' (a live
   * matrix binding still points at this channel — surface it, don't retry).
   */
  async function deleteChannel(id: number): Promise<void> {
    const body: FranchisorChannels_DeleteInput = { id }
    const raw = await httpService.post(
      sistemaUrl('franchisor-channels/delete'),
      body,
      { inputContract: 'FranchisorChannels_DeleteInput', responseContract: 'FranchisorChannels_DeleteResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: FranchisorChannels_DeleteResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'franchisor_channel_delete_failed')
    }
    channels.value = channels.value.filter((c) => c.id !== id)
    log('useFranchisorChannels', 'deleteChannel', 'Canal excluído', { id })
  }

  /**
   * testSend — POST franchisor-channels/test-send. Fires a REAL adapter
   * delivery against `target`. SUCCESS ONLY at the envelope level — the
   * delivery OUTCOME lives inside the returned `delivered`/`detail`, read by
   * the caller, never thrown here (mirrors `useScheduledJobs.runNow`).
   * Refusals: 'channel_not_found', 'provider_not_supported'.
   */
  async function testSend(input: FranchisorChannels_TestSendInput): Promise<FranchisorChannels_TestSendResponseDados> {
    const raw = await httpService.post(
      sistemaUrl('franchisor-channels/test-send'),
      input,
      { inputContract: 'FranchisorChannels_TestSendInput', responseContract: 'FranchisorChannels_TestSendResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: FranchisorChannels_TestSendResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'franchisor_channel_test_send_failed')
    }
    log('useFranchisorChannels', 'testSend', 'Teste de envio concluído', {
      channelId: input.channel_id,
      delivered: resp.dados.delivered,
    })
    return resp.dados
  }

  /**
   * listTriggers — GET franchisor-channels/triggers. The full catalog matrix,
   * each row carrying its current bindings.
   */
  async function listTriggers(): Promise<void> {
    const raw = await httpService.get(
      sistemaUrl('franchisor-channels/triggers'),
      { responseContract: 'FranchisorChannels_TriggersResponse', skipErrorDisplay: false },
    )
    const resp = raw as { dados: FranchisorChannels_TriggersResponseDados }
    triggers.value = resp.dados.rows
    log('useFranchisorChannels', 'listTriggers', 'Matriz de gatilhos carregada', { count: triggers.value.length })
  }

  /**
   * saveTrigger — POST franchisor-channels/trigger-save. `input.id` null/absent
   * = create a new binding. Refusals: 'trigger_unknown', 'trigger_not_implemented',
   * 'channel_type_already_bound', 'unknown_template_var:<name>'.
   */
  async function saveTrigger(input: FranchisorChannels_TriggerSaveInput): Promise<FranchisorChannels_BindingRow> {
    const body: FranchisorChannels_TriggerSaveInput = {
      trigger_key: input.trigger_key,
      channel_id: input.channel_id,
      template_id: input.template_id,
      enabled: input.enabled ?? false,
      recipient_groups: input.recipient_groups ?? [],
      ...(input.id != null ? { id: input.id } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('franchisor-channels/trigger-save'),
      body,
      { inputContract: 'FranchisorChannels_TriggerSaveInput', responseContract: 'FranchisorChannels_BindingResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: FranchisorChannels_BindingResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'franchisor_channel_trigger_save_failed')
    }
    const row = resp.dados.row
    upsertBinding(row)
    log('useFranchisorChannels', 'saveTrigger', 'Binding salvo', { id: row.id, triggerKey: row.trigger_key })
    return row
  }

  /**
   * listTemplates — GET franchisor-channels/templates.
   */
  async function listTemplates(): Promise<void> {
    const raw = await httpService.get(
      sistemaUrl('franchisor-channels/templates'),
      { responseContract: 'FranchisorChannels_TemplatesResponse', skipErrorDisplay: false },
    )
    const resp = raw as { dados: FranchisorChannels_TemplatesResponseDados }
    templates.value = resp.dados.rows
    templateLanguages.value = resp.dados.languages ?? []
    log('useFranchisorChannels', 'listTemplates', 'Templates carregados', { count: templates.value.length })
  }

  /**
   * saveTemplate — POST franchisor-channels/template-save. `id` is always
   * required — every (trigger x channel_type) pair is seeded at boot, there
   * is no "create a template" action. Refusal: 'template_not_found',
   * 'unknown_template_var:<name>'.
   */
  async function saveTemplate(input: FranchisorChannels_TemplateSaveInput): Promise<FranchisorChannels_TemplateRow> {
    const body: FranchisorChannels_TemplateSaveInput = {
      id: input.id,
      body: input.body,
      ...(input.subject != null ? { subject: input.subject } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('franchisor-channels/template-save'),
      body,
      { inputContract: 'FranchisorChannels_TemplateSaveInput', responseContract: 'FranchisorChannels_TemplatesResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: FranchisorChannels_TemplatesResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'franchisor_channel_template_save_failed')
    }
    const row = resp.dados.rows[0]
    upsertTemplate(row)
    log('useFranchisorChannels', 'saveTemplate', 'Template salvo', { id: row.id, triggerKey: row.trigger_key })
    return row
  }

  /**
   * createTemplateVariant — POST franchisor-channels/template-create. Cria a
   * variante de idioma de um par já semeado, copiando o pt-BR.
   * Recusas: 'trigger_unknown', 'language_unsupported',
   * 'template_variant_exists', 'template_not_found'.
   */
  async function createTemplateVariant(input: FranchisorChannels_TemplateCreateInput): Promise<FranchisorChannels_TemplateRow> {
    const raw = await httpService.post(
      sistemaUrl('franchisor-channels/template-create'),
      input,
      { inputContract: 'FranchisorChannels_TemplateCreateInput', responseContract: 'FranchisorChannels_TemplatesResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: FranchisorChannels_TemplatesResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'franchisor_channel_template_create_failed')
    }
    const row = resp.dados.rows[0]
    upsertTemplate(row)
    log('useFranchisorChannels', 'createTemplateVariant', 'Variante criada', { id: row.id, language: row.language })
    return row
  }

  /**
   * previewTemplate — POST franchisor-channels/preview. Renders a stored
   * template with the trigger's example values (plus optional `vars`
   * overrides) and returns the text. Sends NOTHING, so unlike testSend() it
   * works with the subscription gate off and writes no log row.
   * Refusals: 'template_not_found', 'template_error:<token>' (a {{token}} in
   * the stored template that no sample fills — the token is IN the code, so
   * the console can name it).
   */
  async function previewTemplate(input: FranchisorChannels_PreviewInput): Promise<FranchisorChannels_PreviewResponseDados> {
    const body: FranchisorChannels_PreviewInput = {
      template_id: input.template_id,
      ...(input.vars != null ? { vars: input.vars } : {}),
    }
    const raw = await httpService.post(
      sistemaUrl('franchisor-channels/preview'),
      body,
      { inputContract: 'FranchisorChannels_PreviewInput', responseContract: 'FranchisorChannels_PreviewResponse', skipErrorDisplay: false },
    )
    const resp = raw as { success?: boolean; dados: FranchisorChannels_PreviewResponseDados; error?: string | null }
    if (resp.success === false) {
      throw new Error(resp.error ?? 'franchisor_channel_preview_failed')
    }
    log('useFranchisorChannels', 'previewTemplate', 'Preview renderizado', { id: input.template_id })
    return resp.dados
  }

  /**
   * listMessages — GET franchisor-channels/messages (paged log read, newest
   * first). `query.limit` is clamped server-side to [1, 100].
   */
  async function listMessages(query: FranchisorChannels_MessagesQueryInput = {}): Promise<void> {
    const params = new URLSearchParams()
    if (query.trigger_key) params.append('trigger_key', query.trigger_key)
    if (query.status) params.append('status', query.status)
    if (query.limit != null) params.append('limit', String(query.limit))
    const queryString = params.toString()
    const url = sistemaUrl('franchisor-channels/messages') + (queryString ? `?${queryString}` : '')

    const raw = await httpService.get(
      url,
      { responseContract: 'FranchisorChannels_MessagesResponse', skipErrorDisplay: false },
    )
    const resp = raw as { dados: FranchisorChannels_MessagesResponseDados }
    messages.value = resp.dados.rows
    log('useFranchisorChannels', 'listMessages', 'Log de mensagens carregado', { count: messages.value.length })
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    channels.value = []
    triggers.value = []
    templates.value = []
    templateLanguages.value = []
    messages.value = []
  }

  return {
    channels,
    triggers,
    templates,
    templateLanguages,
    messages,
    listChannels,
    saveChannel,
    toggleChannel,
    deleteChannel,
    testSend,
    listTriggers,
    saveTrigger,
    listTemplates,
    saveTemplate,
    createTemplateVariant,
    previewTemplate,
    listMessages,
    _reset,
  }
}
